import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';

import { storeName as layoutModelStore } from './layout.model';
import { storeName as projectModelStore } from './project.model';
import { RootModel } from '../models';
import { LocationGroupTypes, MapLocationTypes } from '../../types';
import { buildHashUrl } from '../../utils';
import {
  DEFAULT_CONTENT_PANEL_DISPLAY_ITEM,
  LOCATION_TRANSPORT,
  MAP_TYPE,
  SHARE_LINK_KEY,
} from '../../commons/constants';
import { storeName as locationGroupEntityStore } from '../entities/locationGroup.entity';
import { storeName as mapLocationEntityStore } from '../entities/mapLocation.entity';
import { storeName as templateCategoryEntityStore } from '../entities/templateCategory.entity';

export const storeName = 'locationModel';

type LocationModelTypes = {
  isMapInitial: boolean;
  transport: { key: string; variable: string };
  groupId: number;
  groupKey: string;
  subGroupId: number;
  subGroupKey: string;
  itemId: number;
  focusItemId: number;
  isShowMobileSlider: boolean;
  isFetchedLocationGroups: boolean;
  isFetchedTemplateCategories: boolean;
  mapType: string;
  mapStyle: string;
  isEnableSwitchSatelliteMode: boolean;
  numberOfDisplayItem: number; // number of content panel display items
};

const initialState: LocationModelTypes = {
  isMapInitial: false,
  transport: LOCATION_TRANSPORT.DRIVING,
  groupId: null,
  groupKey: null,
  subGroupId: null,
  subGroupKey: null,
  itemId: null,
  focusItemId: null,
  isShowMobileSlider: true,
  isFetchedLocationGroups: false,
  isFetchedTemplateCategories: false,
  mapType: MAP_TYPE.ROADMAP.key,
  mapStyle: null,
  isEnableSwitchSatelliteMode: false,
  numberOfDisplayItem: DEFAULT_CONTENT_PANEL_DISPLAY_ITEM,
};

export const locationModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setMapInitial: produce((state, payload: boolean) => {
      state.isMapInitial = payload;
    }),
    setTransport: produce((state, payload: { key: string; variable: string }) => {
      state.transport = payload;
    }),
    setGroupData: produce((state, payload: { id: number; key: string }) => {
      if (!payload) {
        state.groupId = initialState.groupId;
        state.groupKey = initialState.groupKey;

        // reset state
        state.itemId = initialState.itemId;
        state.focusItemId = initialState.focusItemId;
        return;
      }

      state.groupId = payload.id;
      state.groupKey = payload.key;
    }),
    setSubGroupData: produce((state, payload: { id: number; key: string }) => {
      if (!payload) {
        state.subGroupId = initialState.subGroupId;
        state.subGroupKey = initialState.subGroupKey;
        return;
      }

      state.subGroupId = payload.id;
      state.subGroupKey = payload.key;
    }),
    setItemId: produce((state, payload: number) => {
      state.itemId = payload || initialState.itemId;
    }),
    setFocusItemId: produce((state, payload: number) => {
      state.focusItemId = payload || initialState.focusItemId;
    }),
    setShowMobileSlider: produce((state, payload: boolean) => {
      state.isShowMobileSlider = payload;
    }),
    setFetchedLocationGroups: produce((state, payload: boolean) => {
      state.isFetchedLocationGroups = payload || initialState.isFetchedLocationGroups;
    }),
    setFetchedTemplateCategories: produce((state, payload: boolean) => {
      state.isFetchedTemplateCategories = payload || initialState.isFetchedTemplateCategories;
    }),
    setMapType: produce((state, payload: string) => {
      if (!payload) {
        state.mapType = initialState.mapType;
        return;
      }

      switch (payload) {
        case MAP_TYPE.ROADMAP.key:
          state.mapType = MAP_TYPE.HYBRID.key;
          break;
        case MAP_TYPE.HYBRID.key:
          state.mapType = MAP_TYPE.ROADMAP.key;
          break;

        default:
          break;
      }
    }),
    setMapStyle: produce((state, payload: string) => {
      state.mapStyle = payload || initialState.mapStyle;
    }),
    setConfig: produce(
      (
        state,
        payload: { mapStyle: string; satelliteMode: boolean; numberOfDisplayItem?: number }
      ) => {
        state.mapStyle = payload.mapStyle;
        state.isEnableSwitchSatelliteMode = payload.satelliteMode;
        if (payload.numberOfDisplayItem) state.numberOfDisplayItem = payload.numberOfDisplayItem;
      }
    ),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    async fetchLocationData(_, state) {
      if (!state[storeName].isFetchedLocationGroups) {
        dispatch[storeName].setFetchedLocationGroups(true);
      } else return;

      const result: { groups: LocationGroupTypes[]; items: MapLocationTypes[] } = await dispatch[
        locationGroupEntityStore
      ].loadByProjectKey();
      if (!result || !result.groups || !result.groups.length) return;
      dispatch[projectModelStore].setLocationGroupIds(result.groups.map((i) => +i.id));

      if (!result || !result.items || !result.items.length) return;
      dispatch[mapLocationEntityStore].checkTemplateCategory(result.items);
    },
    async fetchTemplateCategory(_, state) {
      if (!state[storeName].isFetchedTemplateCategories) {
        dispatch[storeName].setFetchedTemplateCategories(true);
      } else return;

      await dispatch[templateCategoryEntityStore].loadByProjectKey();
    },
    async fetchLocationGroupById(id: number) {
      const items: MapLocationTypes[] = await dispatch[locationGroupEntityStore].loadById(id);

      if (!items || !items.length) return;
      dispatch[mapLocationEntityStore].checkTemplateCategory(items);
    },
    updateHashParams(payload, state) {
      const {
        groupKey = state[storeName].groupId,
        subGroupKey = state[storeName].subGroupId,
        focusItemKey,
        itemKey,
      } = payload;

      // create new url params object
      const urlParams = {};
      if (groupKey) {
        urlParams[SHARE_LINK_KEY.LOCATION_GROUP] = groupKey;
      }

      if (groupKey && subGroupKey && groupKey !== subGroupKey) {
        urlParams[SHARE_LINK_KEY.LOCATION_SUB_GROUP] = subGroupKey;
      }

      if (focusItemKey) {
        urlParams[SHARE_LINK_KEY.LOCATION_FOCUS_ITEM] = focusItemKey;
      }

      if (itemKey) {
        urlParams[SHARE_LINK_KEY.LOCATION_ITEM] = itemKey;
      }

      buildHashUrl(urlParams);
    },
    async setGroupInfo(id: number, state) {
      if (!id || id === state[storeName].groupId) return;
      dispatch[storeName].setGroupData({ id });
    },
    updateShowMobileSlider(_, state) {
      if (!state[layoutModelStore].isMobileDevice) return;
      dispatch[storeName].setShowMobileSlider(!state[storeName].isShowMobileSlider);
    },
  }),
});

export default locationModel;
